import { UserApplicationPreferencesApiModel } from '../../../api/userApplicationPreferences/models/userApplicationPreferencesApiModel';
import { BasicFilterApiModel } from '../../../api/shared/models/basic-filter.api.model';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { userAppPreferencesDefault } from '../data/user-app-preferences-default';
import {
    UserAppPreferenceMonitoringCommonFilters,
    UserAppPreferenceBriefingsFilters,
    UserAppPreferenceCoverageAlertsFilters,
    UserAppPreferenceDistributionsFilters,
    UserAppPreferenceSavedSearchesFilters,
    UserAppPreferenceSubscriptionsFilters,
    UserAppPreferenceTargetedListsFilters,
    UserAppPreferenceTopicsFilters,
} from './user-app-preferences-filters.model';
import {
    UserAppMonitoringDisplaySettingsModel,
    UserAppSavedCoverageDisplaySettingsModel,
    UserAppTargetingDisplaySettingsModel,
    UserAppBriefingDisplaySettingsModel,
    UserAppSubscriptionDisplaySettingsModel
} from './user-app-default-display-preferences.model';
import { UserAppPreferenceChartColors } from './user-app-preferences-chart-colors.model';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { deepCompare } from '../../utils';
import { UserAppPreferencesVersioner } from '../utils/user-app-preferences-versioner';
import { RelativeTimezoneDateTypeEnum } from '../../../api/mentions/enum/relative-timezone-date-type.enum';

export type UserAppPreferencesVersion = '0.0' | '1.0' | '1.1' | '1.2' | '1.3' | '1.4' | '1.5' | '1.6' | '1.7';

export const currentUserAppPreferencesVersion: UserAppPreferencesVersion = '1.7';

export class UserAppPreferencesModel {
    version: UserAppPreferencesVersion = currentUserAppPreferencesVersion;
    monitoringPrefs: UserAppMonitorPreferencesModel;
    reportsPrefs: UserAppReportPreferencesModel;
    targetingPrefs: UserAppTargetingPreferencesModel;
    socialPrefs: UserAppSocialPreferencesModel;
    accessibilityPrefs?: UserAppAccessibilityPreferences;
    contactsPrefs: UserAppContactsPreferencesModel;
    generalPrefs: UserAppGeneralPreferencesModel;

    constructor(apiModel?: UserApplicationPreferencesApiModel) {
        if (apiModel) {
            const jsonObject = JSON.parse(apiModel.userAppPrefs) as UserAppPreferencesModel;
            const modelFromJson = new UserAppPreferencesVersioner().transformDataToNewestVersion(jsonObject);
            const updatedModel = UserAppPreferencesModel.deepTransfer(modelFromJson, UserAppPreferencesModel.deepClone(userAppPreferencesDefault));

            this.version = updatedModel.version;
            this.monitoringPrefs = updatedModel.monitoringPrefs;
            this.reportsPrefs = updatedModel.reportsPrefs;
            this.targetingPrefs = updatedModel.targetingPrefs;
            this.socialPrefs = updatedModel.socialPrefs;
            this.contactsPrefs = updatedModel.contactsPrefs;
            this.generalPrefs = updatedModel.generalPrefs;
        } else {
            const defaultClone = UserAppPreferencesModel.deepClone(userAppPreferencesDefault);
            this.monitoringPrefs = defaultClone.monitoringPrefs;
            this.reportsPrefs = defaultClone.reportsPrefs;
            this.targetingPrefs = defaultClone.targetingPrefs;
            this.socialPrefs = defaultClone.socialPrefs;
            this.contactsPrefs = defaultClone.contactsPrefs;
            this.generalPrefs = defaultClone.generalPrefs;
        }
    }

    /**
     * Creates a new UserAppPreferencesModel using the values in the jsonObject, falling back to default values if necessary
     */
    private static deepTransfer<T>(jsonObject: T, defaultObject: T): T {
        if (Array.isArray(jsonObject)){
            return jsonObject.map(this.deepClone) as unknown as T;
        } else if (jsonObject === null || jsonObject === undefined) {
            return this.deepClone(defaultObject);
        } else if (defaultObject === null || defaultObject === undefined) {
            return this.deepClone(jsonObject);
        } else if (typeof jsonObject === 'object') {
            // Iterate over all default properties and create a new object using the json value where possible
            return Object.getOwnPropertyNames(defaultObject).reduce((o, prop) => {
                Object.defineProperty(
                    o,
                    prop,
                    // The json object may not have the property (if it is new for instance), so we fall back to default
                    Object.getOwnPropertyDescriptor(jsonObject, prop) ?? Object.getOwnPropertyDescriptor(defaultObject, prop)
                );

                // Special cases for DateQueryModels
                if (prop === 'createdDate' || prop === 'modifiedDate') {
                    o[prop] = this.handleDate(jsonObject[prop] ?? defaultObject[prop]);
                    return o;
                }

                if (prop === 'generalColors') {
                    o[prop] = {...defaultObject[prop], ...jsonObject[prop]};
                    return o;
                }

                o[prop] = this.deepTransfer(
                    (jsonObject as { [key: string]: any })[prop],
                    (defaultObject as { [key: string]: any })[prop]
                );
                return o;
            }, Object.create(Object.getPrototypeOf(defaultObject)));
        }
        return jsonObject as T;
    }

    private static deepClone<T>(source: T): T {
        if (Array.isArray(source)){
            return source.map(this.deepClone) as unknown as T;
        } else if (source instanceof DateQueryModel) {
            return this.handleDate(source) as unknown as T;
        } else if (source && typeof source === 'object') {
            return Object.getOwnPropertyNames(source).reduce((o, prop) => {
                Object.defineProperty(o, prop, Object.getOwnPropertyDescriptor(source, prop));
                if (prop === 'createdDate' || prop === 'modifiedDate') {
                    o[prop] = this.handleDate(source[prop]);
                    return o;
                }

                o[prop] = this.deepClone(source[prop]);
                return o;
            }, Object.create(Object.getPrototypeOf(source)));
        }
        return source as T;
    }

    /**
     * DateQueryModels with ranges get serialized to strings, we need to ensure
     * the ranges are Dates so PrimeNG Calendar can use them properly
     */
    private static handleDate(date: DateQueryModel) {
        if (date.type === DateQueryTypeEnum.ABSOLUTE) {
            const start = new Date(date.absoluteRange.start);
            const end = new Date(date.absoluteRange.end);
            date.absoluteRange.start = start;
            date.absoluteRange.end = end;
        }

        if (date.type === DateQueryTypeEnum.RELATIVE) {
            // deal with old date to be backward compatible
            let relativeTimezoneQuery;
            switch (date.relativeStartOffsetH) {
                case -24:
                    relativeTimezoneQuery = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_24_HOURS);
                    break;
                case -72:
                    relativeTimezoneQuery = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_3_DAYS);
                    break;
                case -168:
                    relativeTimezoneQuery = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_7_DAYS);
                    break;
                case -720:
                    relativeTimezoneQuery = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_30_DAYS);
                    break;
            }

            date = relativeTimezoneQuery;
        }

        return DateQueryModel.clone(date);
    }

    public equals(other: UserAppPreferencesModel): boolean {
        return deepCompare(this, other);
    }

    public toApiModel(): UserApplicationPreferencesApiModel {
        return {
            userAppPrefs: JSON.stringify({
                version: this.version,
                monitoringPrefs: this.monitoringPrefs,
                reportsPrefs: this.reportsPrefs,
                targetingPrefs: this.targetingPrefs,
                socialPrefs: this.socialPrefs,
                contactsPrefs: this.contactsPrefs,
                generalPrefs: this.generalPrefs
            })
        };
    }
}

export class UserAppReportPreferencesModel {
    defaultFilters: UserAppPreferenceMonitoringCommonFilters;
    defaultChartColors: UserAppPreferenceChartColors;
}

export class UserAppMonitorPreferencesModel {
    topicsPrefs: {
        defaultFilters: UserAppPreferenceTopicsFilters;
        defaultDisplaySettings: UserAppMonitoringDisplaySettingsModel;
    };
    savedCoveragePrefs: {
        defaultFilters: UserAppPreferenceMonitoringCommonFilters;
        defaultDisplaySettings: UserAppSavedCoverageDisplaySettingsModel;
    };
    briefingsPrefs: {
        defaultFilters: UserAppPreferenceBriefingsFilters;
        defaultDisplaySettings: UserAppBriefingDisplaySettingsModel;
        defaultBriefingTemplate: string;
    };
    coverageAlertsPrefs: {
        defaultFilters: UserAppPreferenceCoverageAlertsFilters;
    };
    subscriptionsPrefs: {
        defaultFilters: UserAppPreferenceSubscriptionsFilters;
        defaultDisplaySettings: UserAppSubscriptionDisplaySettingsModel;
    };
    defaultExportColumns: string[];
}

export class UserAppTargetingPreferencesModel {
    savedSearchesPrefs: {
        defaultFilters: UserAppPreferenceSavedSearchesFilters;
        defaultSearchSettings: {
            includePrivateContacts: boolean;
            locations: BasicFilterApiModel<number>;
            languages: BasicFilterApiModel<string>;
        };
        defaultDisplaySettings: UserAppTargetingDisplaySettingsModel;
    };
    targetedListsPrefs: {
        defaultFilters: UserAppPreferenceTargetedListsFilters;
        defaultDisplaySettings: UserAppTargetingDisplaySettingsModel;
    };
    distributionsPrefs: {
        defaultFilters: UserAppPreferenceDistributionsFilters;
        defaultDistributionDetails: {
            fromName: string;
            replyTo: string;
        };
        defaultRecipients: string[];
        sentDistributionDisplaySettings: UserAppTargetingDisplaySettingsModel;
        enableMppEstimates: boolean;
        defaultEmailAccount: number;
        // Default footer is handled separately due to separate endpoint in backend
    };
}

export class UserAppSocialPreferencesModel {
    defaultExportColumns: string[];
}

export class UserAppAccessibilityPreferences {
    collapseToolbars: boolean;
    tooltipsOnFocus: boolean;
}

export class UserAppContactsPreferencesModel {
    defaultSelectedCheckboxes: string[];
};

export interface UserAppGeneralPreferencesModel {
    favoriteColors: string[];  // Max 10 hex color strings
    accessibility: UserAppAccessibilityPreferences;
}
